import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  alpha,
  Alert,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axiosInstance from '../../lib/axios';
import { generateSkuFromName } from '../../utils/sku';
import { useSkuValidation } from '../../hooks/useSkuValidation';
import type { Product } from '../../types';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

// Default colors for non-POS contexts
const defaultColors: POSColors = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceLight: '#F1F5F9',
  border: '#E2E8F0',
  textPrimary: '#1E293B',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  accent: '#2563EB',
  accentLight: '#3B82F6',
};

interface InlineProductCreateModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** The barcode to pre-populate (from scanner) */
  barcode: string;
  /** Callback when product is successfully created */
  onProductCreated: (product: Product) => void;
  /** Optional theme colors (for POS context) */
  colors?: POSColors;
}

interface FormErrors {
  name?: string;
  sku?: string;
  price?: string;
}

// Available product categories
const PRODUCT_CATEGORIES = [
  'Electronics',
  'Clothing',
  'Food',
  'Books',
  'Home & Garden',
  'Sports',
  'Toys',
  'Beauty',
  'Other',
];

/**
 * Modal for quick product creation with barcode pre-populated.
 * Provides a minimal form with required fields: name, SKU, price.
 * SKU is auto-generated from product name.
 */
export const InlineProductCreateModal: React.FC<InlineProductCreateModalProps> = ({
  open,
  onClose,
  barcode,
  onProductCreated,
  colors = defaultColors,
}) => {
  // Form fields
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');

  // Form state
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Track if SKU was manually edited
  const [skuManuallyEdited, setSkuManuallyEdited] = useState(false);

  // Real-time SKU availability validation
  const { isAvailable: isSkuAvailable, isChecking: isCheckingSku } = useSkuValidation({
    sku,
    enabled: sku.length >= 3,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setName('');
      setSku('');
      setPrice('');
      setCategory('');
      setErrors({});
      setApiError(null);
      setIsLoading(false);
      setSkuManuallyEdited(false);
    }
  }, [open]);

  // Auto-generate SKU from name (only if not manually edited)
  const handleNameChange = (value: string) => {
    setName(value);
    if (!skuManuallyEdited) {
      const generatedSku = generateSkuFromName(value);
      setSku(generatedSku);
    }
    // Clear name error when typing
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Handle manual SKU editing
  const handleSkuChange = (value: string) => {
    setSku(value.toUpperCase());
    setSkuManuallyEdited(true);
    // Clear SKU error when typing
    if (errors.sku) {
      setErrors((prev) => ({ ...prev, sku: undefined }));
    }
  };

  // Handle price change with validation
  const handlePriceChange = (value: string) => {
    // Only allow numbers and decimal point
    const sanitized = value.replace(/[^0-9.]/g, '');
    setPrice(sanitized);
    // Clear price error when typing
    if (errors.price) {
      setErrors((prev) => ({ ...prev, price: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Le nom du produit est requis';
    }

    if (!sku.trim()) {
      newErrors.sku = 'Le SKU est requis';
    } else if (isSkuAvailable === false) {
      newErrors.sku = 'Ce SKU existe déjà';
    }

    if (!price.trim()) {
      newErrors.price = 'Le prix est requis';
    } else {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'Le prix doit être un nombre positif';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await axiosInstance.post('/products', {
        name: name.trim(),
        sku: sku.trim().toUpperCase(),
        price: parseFloat(price),
        barcode: barcode.trim(),
        category: category || undefined,
        isVisibleInStorefront: true,
        productType: 'sellable',
      });

      const newProduct = response.data;
      onProductCreated(newProduct);
      // Note: Don't call onClose() - the parent's onProductCreated callback handles dialog closure
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Impossible de créer le produit';
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine SKU field adornment based on validation state
  const getSkuAdornment = () => {
    if (!sku || sku.length < 3) return null;
    if (isCheckingSku) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={16} sx={{ color: colors.textMuted }} />
        </InputAdornment>
      );
    }
    if (isSkuAvailable === true) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon sx={{ color: '#10B981', fontSize: 18 }} />
        </InputAdornment>
      );
    }
    if (isSkuAvailable === false) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 18 }} />
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: alpha(colors.accent, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AddCircleOutlineIcon sx={{ color: colors.accent, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: colors.textPrimary,
            }}
          >
            Créer un produit
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.textMuted,
              fontFamily: 'monospace',
            }}
          >
            Code-barres: {barcode}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {apiError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: alpha('#EF4444', 0.1),
              color: colors.textPrimary,
              '& .MuiAlert-icon': {
                color: '#EF4444',
              },
            }}
          >
            {apiError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nom du produit"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
            required
            autoFocus
            sx={getTextFieldStyles(colors)}
          />

          <TextField
            label="SKU"
            value={sku}
            onChange={(e) => handleSkuChange(e.target.value)}
            error={!!errors.sku || isSkuAvailable === false}
            helperText={
              errors.sku ||
              (isSkuAvailable === false && 'Ce SKU existe déjà') ||
              'Généré automatiquement depuis le nom'
            }
            fullWidth
            size="small"
            required
            InputProps={{
              endAdornment: getSkuAdornment(),
            }}
            sx={getTextFieldStyles(colors)}
          />

          <TextField
            label="Prix (DZD)"
            value={price}
            onChange={(e) => handlePriceChange(e.target.value)}
            error={!!errors.price}
            helperText={errors.price}
            fullWidth
            size="small"
            required
            type="text"
            inputMode="decimal"
            sx={getTextFieldStyles(colors)}
          />

          <TextField
            label="Catégorie"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            size="small"
            select
            sx={getTextFieldStyles(colors)}
          >
            <MenuItem value="">
              <em>Aucune</em>
            </MenuItem>
            {PRODUCT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>

          {/* Barcode display (read-only, pre-populated) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              backgroundColor: alpha(colors.accent, 0.08),
              borderRadius: '10px',
              border: `1px solid ${alpha(colors.accent, 0.2)}`,
            }}
          >
            <CheckCircleIcon sx={{ color: colors.accent, fontSize: 18 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: colors.textMuted,
                }}
              >
                Code-barres (pré-rempli)
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                  fontFamily: 'monospace',
                  letterSpacing: '0.03em',
                }}
              >
                {barcode}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            '&:hover': {
              borderColor: colors.textMuted,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isCheckingSku || isSkuAvailable === false}
          sx={{
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentLight,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(colors.accent, 0.5),
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: '#FFF' }} />
          ) : (
            'Créer'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function for text field styles
const getTextFieldStyles = (colors: POSColors) => ({
  '& .MuiInputLabel-root': {
    color: colors.textMuted,
    fontSize: '14px',
    '&.Mui-focused': {
      color: colors.accent,
    },
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceLight,
    borderRadius: '10px',
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.textMuted,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accent,
    },
    '&.Mui-error fieldset': {
      borderColor: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: colors.textPrimary,
    fontSize: '14px',
  },
  '& .MuiFormHelperText-root': {
    color: colors.textMuted,
    fontSize: '12px',
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
  '& .MuiSelect-select': {
    color: colors.textPrimary,
  },
});

export default InlineProductCreateModal;
